// resources/js/Pages/Formula/questions/SchemaImportExport.js
import { useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import { toast } from 'react-toastify';

/**
 * Custom hook for schema import/export functionality
 */
export function useSchemaImportExport() {
  
  // Generate UUIDs for all nodes recursively
  const addUuidsToSchema = useCallback((node) => {
    if (!node || typeof node !== 'object') return node;
    
    return {
      ...node,
      id: uuid(),
      children: node.children ? node.children.map(child => addUuidsToSchema(child)) : undefined
    };
  }, []);

  // Remove UUIDs and any non-schema fields for clean export
  const cleanSchemaForExport = useCallback((node) => {
    if (!node || typeof node !== 'object') return node;
    
    // Only keep schema-related fields, remove UUIDs and any context settings
    const { 
      id, 
      context_inclusion, 
      include_character_descriptions, 
      ...cleanNode 
    } = node;
    
    return {
      ...cleanNode,
      children: cleanNode.children ? cleanNode.children.map(cleanSchemaForExport) : undefined
    };
  }, []);

  // Schema validation — accepts all supported node types, value types, and properties
  const validateImportedSchema = useCallback((schema) => {
    const errors = [];

    const VALID_TYPES = ['list', 'array', 'field', 'section', 'text-block', 'divider', 'graph', 'row_detail'];
    const VALID_VALUE_TYPES = ['string', 'number', 'score', 'importance', 'badge', 'scene_number', 'scene_list', 'questions_for_llm'];
    const VALID_DISPLAYS = ['ul', 'comma', 'table', 'tabs', 'cards'];
    const VALID_STYLES = ['warning', 'danger', 'info', 'success', 'action', 'neutral'];

    const validateNode = (node, path = '') => {
      if (!node || typeof node !== 'object') {
        errors.push(`${path}: Invalid node structure`);
        return;
      }

      const type = node.type;

      // text-block and divider don't need a key
      if (!['text-block', 'divider'].includes(type)) {
        if (!node.key || typeof node.key !== 'string') {
          errors.push(`${path}: Missing or invalid 'key' field`);
        }
      }

      if (!type || !VALID_TYPES.includes(type)) {
        errors.push(`${path}: Invalid 'type' "${type}" — must be one of: ${VALID_TYPES.join(', ')}`);
        return; // Can't validate further without a valid type
      }

      // Field validation
      if (type === 'field') {
        if (node.valueType && !VALID_VALUE_TYPES.includes(node.valueType)) {
          errors.push(`${path}: Invalid 'valueType' "${node.valueType}" — must be one of: ${VALID_VALUE_TYPES.join(', ')}`);
        }
        if (node.style && !VALID_STYLES.includes(node.style)) {
          errors.push(`${path}: Invalid 'style' "${node.style}" — must be one of: ${VALID_STYLES.join(', ')}`);
        }
        if (node.children && node.children.length > 0) {
          errors.push(`${path}: Field nodes cannot have children`);
        }
      }

      // List/Array validation
      if (type === 'list' || type === 'array') {
        if (node.display && !VALID_DISPLAYS.includes(node.display)) {
          errors.push(`${path}: Invalid 'display' "${node.display}" — must be one of: ${VALID_DISPLAYS.join(', ')}`);
        }
        if (node.children) {
          node.children.forEach((child, index) => {
            validateNode(child, `${path}.children[${index}]`);
          });
        }
      }

      // Section validation
      if (type === 'section') {
        if (!node.title && !node.key) {
          errors.push(`${path}: Section needs a 'title' or 'key'`);
        }
        if (node.children) {
          node.children.forEach((child, index) => {
            validateNode(child, `${path}.children[${index}]`);
          });
        }
      }

      // row_detail validation
      if (type === 'row_detail') {
        if (node.children) {
          node.children.forEach((child, index) => {
            validateNode(child, `${path}.children[${index}]`);
          });
        }
      }
    };

    validateNode(schema, 'root');
    return errors;
  }, []);

  // Process imported JSON into valid schema (schema only, no context)
  const processImportedJson = useCallback((jsonString) => {
    try {
      // Parse JSON
      let parsedSchema;
      try {
        parsedSchema = JSON.parse(jsonString);
      } catch (e) {
        return { success: false, errors: ['Invalid JSON format'] };
      }
      
      // Handle root wrapping if needed
      let finalSchema;
      if (parsedSchema.key === 'Root' && parsedSchema.type === 'list') {
        // Already has root - clean any context settings that might be mixed in
        const { context_inclusion, include_character_descriptions, ...cleanRoot } = parsedSchema;
        finalSchema = cleanRoot;
      } else if (Array.isArray(parsedSchema)) {
        // Array of nodes - wrap in root
        finalSchema = {
          key: 'Root',
          type: 'list',
          display: 'ul',
          children: parsedSchema
        };
      } else {
        // Single node - wrap in root
        finalSchema = {
          key: 'Root', 
          type: 'list',
          display: 'ul',
          children: [parsedSchema]
        };
      }
      
      // Validate the schema
      const validationErrors = validateImportedSchema(finalSchema);
      if (validationErrors.length > 0) {
        return { success: false, errors: validationErrors };
      }
      
      // Add UUIDs to all nodes
      const schemaWithIds = addUuidsToSchema(finalSchema);
      
      return { success: true, schema: schemaWithIds };
      
    } catch (error) {
      return { success: false, errors: [`Import failed: ${error.message}`] };
    }
  }, [addUuidsToSchema, validateImportedSchema]);

  // Export schema to downloadable file (schema only, no context)
  const exportSchemaToFile = useCallback((schema, filename = 'schema') => {
    if (!schema) {
      toast.warning('No schema to export');
      return;
    }
    
    const exportData = cleanSchemaForExport(schema);
    const jsonString = JSON.stringify(exportData, null, 2);
    
    // Download as file
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}-schema.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Schema exported!');
  }, [cleanSchemaForExport]);

  return {
    processImportedJson,
    exportSchemaToFile,
    validateImportedSchema,
    addUuidsToSchema,
    cleanSchemaForExport
  };
}

/**
 * Get example schemas for documentation/help (pure schema, no context)
 */
export const getExampleSchemas = () => {
  return {
    simple: {
      key: "Root",
      type: "list",
      display: "ul",
      children: [
        {
          key: "summary",
          type: "field",
          valueType: "string",
          instruction: "Brief summary of the analysis"
        },
        {
          key: "score",
          type: "field", 
          valueType: "number",
          instruction: "Numeric score from 1-10"
        }
      ]
    },
    
    withTable: {
      key: "Root",
      type: "list",
      display: "ul",
      children: [
        {
          key: "overview",
          type: "field",
          valueType: "string",
          instruction: "Overall assessment"
        },
        {
          key: "scenes",
          type: "list",
          display: "table",
          children: [
            {
              key: "scene_number",
              type: "field",
              valueType: "scene_number",
              instruction: "Scene number where this occurs"
            },
            {
              key: "description",
              type: "field",
              valueType: "string", 
              instruction: "What happens in this scene"
            },
            {
              key: "rating",
              type: "field",
              valueType: "number",
              instruction: "Rating from 1-5"
            }
          ]
        }
      ]
    },

    withTabs: {
      key: "Root",
      type: "list",
      display: "tabs",
      children: [
        {
          key: "strengths",
          type: "list",
          display: "ul",
          children: [
            {
              key: "description",
              type: "field",
              valueType: "string",
              instruction: "What works well"
            }
          ]
        },
        {
          key: "weaknesses", 
          type: "list",
          display: "ul",
          children: [
            {
              key: "description",
              type: "field",
              valueType: "string",
              instruction: "What needs improvement"
            }
          ]
        }
      ]
    },

    // ✅ NEW: Add cards example to showcase the fixed validation
    withCards: {
      key: "Root",
      type: "list",
      display: "ul",
      children: [
        {
          key: "characters",
          type: "list",
          display: "cards",
          children: [
            {
              key: "name",
              type: "field",
              valueType: "string",
              instruction: "Character name (will be card title)"
            },
            {
              key: "role",
              type: "field",
              valueType: "string",
              instruction: "Character's role in the story"
            },
            {
              key: "scene_appearances",
              type: "field",
              valueType: "scene_list",
              instruction: "Scenes where this character appears"
            }
          ]
        }
      ]
    }
  };
};